import { IconEdit, IconPlus, IconTrash, IconUsers } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ErrorMessage from '../ErrorMessage';

import { ActionIcon, Button, Card, Checkbox, Grid, Group, Modal, Switch, Tabs, Text, Textarea, TextInput, Title } from '@mantine/core';
import useFetch from '../../helpers/useFetch';

import ConfirmationModal from './ConfirmationModal';

const AiChatFastContextModal = ({
  brandContext,
  selectedContext,
  setSelectedContext,
  closeModal,
  selectedText,
  setSelectedText,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { domain } = useSelector((state) => state.domain);
  const textareaRef = useRef(null);
  const [coPilotOptionsVisible, setCoPilotOptionsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(selectedText ? 'savedPrompts' : 'brandContext');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({ name: '', index: null });
  const [userPrompts, setUserPrompts] = useState([]);
  const [userPromptsError, setUserPromptsError] = useState(false);
  const [editableId, setEditableId] = useState(null);
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');

  const fetch = useFetch();
  const coPilotOptions = [
    {
      title: 'No Co-pilot',
      description: '',
    },
    {
      title: 'Releva Co-pilot',
      description: 'Helping you with questions about Releva and how to use it.',
    },
    {
      title: 'Image prompt Co-pilot',
      description: 'Helping you with generation and enhancing image prompt.',
    },
    {
      title: 'Email Co-pilot',
      description: 'Guiding the AI in creating emails based on the best practices.',
    },
    {
      title: 'Psychology Co-pilot',
      description: 'Helping you to generated text taking in account psychological traits and theory.',
    },
    {
      title: 'Pop-up Co-pilot',
      description: 'Created to guide the AI in crafting compelling pop-up messages.',
    },
    {
      title: 'SMS Co-pilot',
      description: 'Formulated to guide the AI model in writing effective and concise SMS messages.',
    },
    {
      title: 'Testing Co-pilot',
      description:
        'Designed to instruct the AI on how to create testing scenarios, focusing on variations and performance metrics. ',
    },
  ];
  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/userPrompts`)
      .then(({ userPrompts }) => {
        setUserPrompts(userPrompts);
      })
      .catch((error) => {
        setUserPromptsError(error);
      });
  }, [fetch, activeTab, domain]);

  const create = (userPrompt) => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/userPrompts`, {
      method: 'POST',
      body: JSON.stringify(userPrompt),
    })
      .then(({ userPrompt }) => {
        if (userPrompt && userPrompt.id) {
          setUserPrompts((prev) => [userPrompt, ...prev]);
        } else {
          setUserPromptsError({ message: 'Failed to create item' });
        }
      })
      .catch((error) => {
        setUserPromptsError(error);
      });
  };
  const update = (userPrompt) => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/userPrompts/${editableId}`, {
      method: 'PUT',
      body: JSON.stringify(userPrompt),
    })
      .then(({ response }) => {
        setUserPrompts((userPrompts) =>
          userPrompts.map((prompt) =>
            prompt.id === userPrompt.id
              ? {
                  ...prompt,
                  title: userPrompt.title,
                  description: userPrompt.description,
                  status: userPrompt.status,
                  updatedAt: userPrompt.updatedAt,
                }
              : prompt
          )
        );
      })
      .catch((error) => {
        setUserPromptsError(error);
      });
  };
  const selectOption = (title) => {
    setSelectedContext((prev) => ({ ...prev, coPilots: [title] }));
  };

  const handleTabClick = (type) => {
    setActiveTab(type);
  };

  const handleSelectionChange = (value, name, isChecked) => {
    setSelectedContext((prev) => {
      let newSelectedContext = { ...prev };

      newSelectedContext[name] = newSelectedContext[name] || [];

      if (isChecked) {
        newSelectedContext[name].push(value);
        return newSelectedContext;
      }

      newSelectedContext[name] = newSelectedContext[name].filter((val) => val !== value);
      if (!newSelectedContext[name].length) {
        delete newSelectedContext[name];
      }

      return newSelectedContext;
    });
  };

  const handleAddItem = (e, name) => {
    e.preventDefault();

    if (editableId) {
      let item = userPrompts.find((p) => p.id === editableId);
      item.title = e.target.itemTitle.value || item.title;
      item.description = e.target.itemDescription.value || item.description;
      update(item);
    } else {
      const newItem = {
        title: e.target.itemTitle.value,
        description: e.target.itemDescription.value,
        shared: false,
        userId: user.id,
      };
      create(newItem);
    }
    if (selectedText) {
      setSelectedText('');
    }
    e.target.reset();
    setEditableId(null);
    setTitle('');
    setText('');
  };

  const handleDeleteItem = () => {
    const index = deleteInfo;

    const itemToDelete = userPrompts.find((item) => item.id === index);
    if (!itemToDelete) return;

    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/userPrompts/${index}`, {
      method: 'DELETE',
    })
      .then(() => {
        setUserPrompts((prevPrompts) => prevPrompts.filter((prompt) => prompt.id !== index));
        setUserPromptsError({});
      })
      .catch((error) => {
        setUserPromptsError(error);
      });
    setConfirmDeleteOpen(false);
  };

  const handleShareItem = (index) => {
    const updatedUserPrompt = userPrompts.find((item) => item.id === index);
    const updatedPrompt = { ...updatedUserPrompt, shared: !updatedUserPrompt.shared };
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/userPrompts/${index}`, {
      method: 'PUT',
      body: JSON.stringify(updatedPrompt),
    }).then(({ response }) => {
      setUserPrompts((userPrompts) =>
        userPrompts.map((prompt) => (prompt.id === index ? { ...prompt, shared: !prompt.shared } : prompt))
      );
      setUserPromptsError({});
    });
  };
  const handleEditClick = (value) => {
    setEditableId(value.id);
    setTitle(value.title);
    setText(value.description);
    if (textareaRef.current) {
      textareaRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      textareaRef.current.focus();
    }
  };
  const confirmDeleteItem = (index) => {
    setDeleteInfo(index);
    setConfirmDeleteOpen(true);
  };
  const handleCancel = () => {
    setTitle('');
    setText('');
    setEditableId(null);
  };

  const renderSectionCards = (name, data) => {
    if (name === 'audiences') {
      data = data.map((value) => `${value.title} - ${value.description}`);
    }
    if (name === 'userPrompts') {
      data = userPrompts;
    }
    return (
      <>
        {data?.map((value, index) => (
          <Card key={`${name}-${index}`} className="brand-context-options" mb="sm" pb="sm" withBorder>
            <Grid align="center" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)', paddingBottom: 8, paddingTop: 8 }}>
              <Grid.Col span={{ base: 12, md: 10 }}>
                <Checkbox
                  id={`${name}-${index}`}
                  checked={selectedContext?.[name]?.includes(value) || false}
                  onChange={(e) => handleSelectionChange(value, name, e.currentTarget.checked)}
                  label={
                    <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
                      {typeof value === 'object' && value.title && value.description ? (
                        <span>
                          <strong>{value.title}</strong>
                        </span>
                      ) : (
                        `${value}`
                      )}
                    </div>
                  }
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 2 }}>
                <Group gap="xs">
                  {name === 'userPrompts' && value.userId === user.id && (
                    <Group gap="xs" title="Allow other users to use, edit, and delete the prompt">
                      <IconUsers
                        size={16}
                        style={{ color: value.shared ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-5)' }}
                      />
                      <Switch
                        size="sm"
                        checked={value.shared}
                        onChange={() => handleShareItem(value.id)}
                      />
                    </Group>
                  )}

                  {(value.shared || value.userId === user.id) && (
                    <>
                      <ActionIcon size="sm" variant="light" onClick={() => handleEditClick(value)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="light" color="red" onClick={() => confirmDeleteItem(value.id)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </>
                  )}
                </Group>
              </Grid.Col>
            </Grid>

            <div style={{ paddingTop: 8, wordWrap: 'break-word', wordBreak: 'break-word' }}>
              {typeof value === 'object' && value.description ? <span>{value.description}</span> : `${value}`}
            </div>
          </Card>
        ))}
      </>
    );
  };

  const currentCoPilot = selectedContext?.coPilots?.[0];

  return (
    <>
      <Modal size="xl" opened={true} onClose={closeModal} title="Use Co-Pilot or Include Context / Prompt">
        <div className="copilot-select" onClick={() => setCoPilotOptionsVisible((prev) => !prev)}>
          <div className="select-box">
            {currentCoPilot && currentCoPilot !== 'No Co-pilot' ? (
              <>
                <div className="option-title">{currentCoPilot}</div>

                <div className="option-text">
                  {coPilotOptions.find((o) => o.title === currentCoPilot).description}
                </div>
              </>
            ) : (
              `Choose Co-Pilot to Use (Optional)`
            )}
          </div>

          <div className="options-container" style={{ display: coPilotOptionsVisible ? 'block' : 'none' }}>
            {coPilotOptions.map((coPilot, index) => (
              <div key={index} className="option" onClick={() => selectOption(coPilot.title)}>
                <div className="option-title">{coPilot.title}</div>
                <div className="option-text">{coPilot.description}</div>
              </div>
            ))}
          </div>
        </div>

        <Tabs value={activeTab} onChange={handleTabClick} variant="default">
          <Tabs.List>
            <Tabs.Tab value="brandContext">Brand Identity</Tabs.Tab>
            <Tabs.Tab value="savedPrompts">Saved Prompts</Tabs.Tab>
          </Tabs.List>
        </Tabs>
        <ErrorMessage message={userPromptsError?.message} />

        <Card withBorder style={{ marginTop: 0, borderRadius: 0, marginBottom: 0, padding: 0 }}>
          <div style={{ maxHeight: '60vh', overflowY: 'scroll', padding: 'var(--mantine-spacing-md)' }}>
            {activeTab === 'brandContext' && (
              <div>
                {brandContext &&
                  typeof brandContext === 'object' &&
                  Object.keys(brandContext)
                    .filter(
                      (key) =>
                        key === 'description' ||
                        key === 'archetypes' ||
                        key === 'personas' ||
                        key === 'tones' ||
                        key === 'voices' ||
                        key === 'audiences'
                    )
                    .map((section) => (
                      <div className="brand-context-options" key={section}>
                        <Title order={5}>{section.charAt(0).toUpperCase() + section.slice(1)}</Title>

                        <hr style={{ marginTop: 0 }} />

                        {Array.isArray(brandContext[section].value) ? (
                          renderSectionCards(section, brandContext[section].value)
                        ) : (
                          <Card withBorder mb="sm" p="sm">
                            <Checkbox
                              id={section}
                              label={`${brandContext[section].value}`}
                              checked={selectedContext?.[section]?.includes(brandContext[section].value) || false}
                              onChange={(e) =>
                                handleSelectionChange(brandContext[section].value, section, e.currentTarget.checked)
                              }
                            />
                          </Card>
                        )}
                      </div>
                    ))}
              </div>
            )}

            {activeTab === 'savedPrompts' && (
              <>
                {renderSectionCards('userPrompts', [])}

                {Boolean(userPrompts.length) && <hr style={{ marginTop: 16, marginBottom: 16 }} />}

                <form onSubmit={(e) => handleAddItem(e, 'userPrompts')}>
                  <div style={{ marginBottom: 12 }}>
                    <TextInput
                      placeholder="Title"
                      name="itemTitle"
                      mb="sm"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                    <Textarea
                      ref={textareaRef}
                      placeholder="Prompt"
                      name="itemDescription"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      rows={15}
                      required
                    />
                    <Group justify="flex-end" mt="sm">
                      {editableId ? (
                        <>
                          <Button variant="default" onClick={() => handleCancel()}>
                            Cancel
                          </Button>
                          <Button type="submit">Edit Prompt</Button>
                        </>
                      ) : (
                        <Button type="submit" leftSection={<IconPlus size={16} />}>
                          Add New Prompt
                        </Button>
                      )}
                    </Group>
                  </div>
                </form>
              </>
            )}
          </div>
        </Card>
      </Modal>

      <ConfirmationModal
        show={confirmDeleteOpen}
        close={() => setConfirmDeleteOpen(false)}
        confirm={handleDeleteItem}
        message="Are you sure you want to delete this custom user prompt?"
      />
    </>
  );
};

export default AiChatFastContextModal;
